import React, { useState } from "react";
import useSWR from "swr";
import {
  Button,
  Checkbox,
  Empty,
  Pagination,
  Popconfirm,
  Space,
  Spin,
  Tag,
  Typography,
  message,
} from "antd";
import {
  getCommunityCommentThread,
  forceDeleteCommunityComment,
} from "../../api/domainApi";
import { CmntyAdminCommentListItem } from "../../types/domain";
import AdminCommentModal, { AdminCommentTarget } from "./AdminCommentModal";
import { AuditLogBadge } from "./AuditLogTimeline";

/** 페이징 단위는 **최상위 댓글**. 답글은 부모와 함께 딸려 오므로 개수에 안 센다. */
const ROOT_PAGE_SIZE = 20;

const authorLabel = (c: CmntyAdminCommentListItem) => {
  // 관리자 댓글은 user_id 가 null 이고 명의는 매핑에 있다 (2026-08-04).
  if (c.authorUserId == null) {
    return (
      <Space size={4}>
        <Tag color="purple" style={{ marginInlineEnd: 0 }}>
          운영자
        </Tag>
        <span style={{ fontWeight: 600 }}>{c.adminAuthorName ?? "-"}</span>
      </Space>
    );
  }
  return (
    <span style={{ fontWeight: 600 }}>
      {c.authorNickname ? `${c.authorNickname} (#${c.authorUserId})` : `#${c.authorUserId}`}
    </span>
  );
};

/**
 * 게시글 상세 안에서 보는 댓글 스레드.
 *
 * 관리자 댓글은 "읽고 답하는" 행위라 본문 옆에 있어야 한다 — 별도 페이지로 이동하면
 * 무엇에 답하는지 모르는 채로 쓰게 된다. 목록 페이지(`CommunityCommentPage`)는
 * 전체 검색·삭제 같은 모더레이션 용도로 남는다.
 *
 * 🔴 **트리를 여기서 조립하지 않는다** (2026-08-07). 예전엔 평면 목록을 size=200 으로 받아
 * 클라이언트에서 부모-자식을 이어 붙였는데, 서버 상한(100)에 걸려 잘리면 *가장 오래된* =
 * 최상위 댓글부터 사라지고 부모를 잃은 답글이 화면에서 통째로 증발했다
 * (dev 게시글 72: 실제 138건 → 화면엔 21건). 지금은 서버가 최상위 기준으로 페이징하고
 * 답글을 부모와 함께 내려주므로 고아가 구조적으로 생길 수 없다.
 */
const PostCommentThread: React.FC<{
  postId: number;
  /** 댓글 수가 바뀌면 게시글 목록/상세를 갱신하도록 알린다 */
  onChanged?: () => void;
  /** 넘치면 스크롤. 댓글이 많은 글에선 아래 "변경 이력"·"조치" 가 화면 밖으로 밀린다 */
  maxHeight?: number | string;
}> = ({ postId, onChanged, maxHeight }) => {
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [page, setPage] = useState(1);
  const [target, setTarget] = useState<AdminCommentTarget | null>(null);

  const swrKey = `/community/posts/${postId}/thread?del=${includeDeleted}&p=${page}`;
  const { data, isLoading, mutate } = useSWR(swrKey, () =>
    getCommunityCommentThread(postId, {
      page: page - 1,
      size: ROOT_PAGE_SIZE,
      includeDeleted,
    }),
  );

  const thread = data?.content ?? [];
  const rootTotal = data?.page?.totalElements ?? 0;
  const replyCountInPage = thread.reduce((n, t) => n + t.replies.length, 0);

  const refresh = () => {
    mutate();
    onChanged?.();
  };

  const remove = async (commentId: number) => {
    try {
      await forceDeleteCommunityComment(commentId);
      message.success("삭제됨");
      refresh();
    } catch (e: any) {
      message.error(e.message);
    }
  };

  const renderItem = (c: CmntyAdminCommentListItem) => (
    <div
      key={c.commentId}
      style={{
        padding: "8px 0",
        borderTop: "1px solid #f5f5f5",
        opacity: c.delYn ? 0.5 : 1,
      }}
    >
      <Space size={8} wrap>
        {authorLabel(c)}
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          #{c.commentId} · {c.createdAt?.replace("T", " ").slice(0, 16)}
        </Typography.Text>
        {/* 표식은 contentModifiedAt·delYn 으로만 그린다 — 목록 조회가 이력 테이블을 안 건드린다.
            실제 이력은 관리자가 눌렀을 때만 가져온다. */}
        {c.delYn ? (
          <AuditLogBadge
            targetType="COMMENT"
            targetId={c.commentId}
            label="삭제"
            color="red"
          />
        ) : (
          c.contentModifiedAt && (
            <AuditLogBadge
              targetType="COMMENT"
              targetId={c.commentId}
              label="수정됨"
            />
          )
        )}
      </Space>
      <div style={{ whiteSpace: "pre-wrap", margin: "4px 0 6px" }}>{c.content}</div>
      <Space size={4}>
        {/* 답글은 최상위 댓글에만 (답글에 답글 불가) */}
        {!c.parentCommentId && !c.delYn && (
          <Button
            size="small"
            onClick={() =>
              setTarget({
                mode: "reply",
                parentCommentId: c.commentId,
                postId,
                parentContent: c.content,
                parentAuthor: c.authorUserId == null ? (c.adminAuthorName ?? "운영자") : (c.authorNickname ?? `#${c.authorUserId}`),
              })
            }
          >
            답글
          </Button>
        )}
        {/* 수정은 관리자가 쓴 댓글만 — 서버도 403 으로 막는다 */}
        {c.authorUserId == null && !c.delYn && (
          <Button
            size="small"
            onClick={() =>
              setTarget({ mode: "edit", commentId: c.commentId, initialContent: c.content })
            }
          >
            수정
          </Button>
        )}
        <Popconfirm title="댓글을 강제 삭제하시겠습니까?" onConfirm={() => remove(c.commentId)}>
          <Button size="small" danger disabled={c.delYn}>
            삭제
          </Button>
        </Popconfirm>
      </Space>
    </div>
  );

  return (
    <>
      <Space style={{ marginBottom: 8 }} wrap>
        <Button type="primary" size="small" onClick={() => setTarget({ mode: "comment", postId })}>
          관리자 댓글 쓰기
        </Button>
        <Checkbox
          checked={includeDeleted}
          onChange={(e) => {
            setIncludeDeleted(e.target.checked);
            setPage(1);
          }}
        >
          삭제 포함
        </Checkbox>
        {/* 페이징 단위가 최상위라, 총계도 최상위 기준임을 분명히 적는다.
            예전엔 전체 건수(답글 포함)를 보여줬는데 화면에 그만큼 안 나와 혼란스러웠다. */}
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          댓글 {rootTotal}건
          {replyCountInPage > 0 && ` · 이 페이지 답글 ${replyCountInPage}건`}
        </Typography.Text>
      </Space>

      {/* 스크롤 대상은 목록뿐 — "관리자 댓글 쓰기" 버튼과 페이지네이션은 바깥에 둔다. */}
      <div
        style={
          maxHeight ? { maxHeight, overflowY: "auto", paddingRight: 6 } : undefined
        }
      >
        {isLoading ? (
          <Spin />
        ) : thread.length === 0 ? (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="댓글 없음" />
        ) : (
          thread.map(({ comment, replies }) => (
            <div key={comment.commentId}>
              {renderItem(comment)}
              {replies.length > 0 && (
                <div style={{ marginLeft: 24 }}>{replies.map((r) => renderItem(r))}</div>
              )}
            </div>
          ))
        )}
      </div>

      {rootTotal > ROOT_PAGE_SIZE && (
        <div style={{ marginTop: 8, textAlign: "right" }}>
          <Pagination
            size="small"
            current={page}
            pageSize={ROOT_PAGE_SIZE}
            total={rootTotal}
            onChange={setPage}
            showSizeChanger={false}
            showTotal={(t) => `최상위 ${t}건`}
          />
        </div>
      )}

      <AdminCommentModal
        open={target !== null}
        target={target}
        onClose={() => setTarget(null)}
        onDone={refresh}
      />
    </>
  );
};

export default PostCommentThread;
